<template>
    <div>
        <div v-if="showRoomType" class="row">
            <div class="col-md-4 mb-2">
                <label class="form-label">Room Type</label>
                <v-select
                    placeholder="All room types"
                    v-model="selectedRoomType"
                    :options="roomTypes"
                    :reduce="type => type.id"
                    label="name"
                />
            </div>
        </div>

        <div class="d-flex flex-wrap gap-3 availability-legend mb-2">
            <span><i class="legend-dot free"></i> Available</span>
            <span><i class="legend-dot reserved"></i> Reserved</span>
            <span><i class="legend-dot occupied"></i> Occupied</span>
            <span><i class="legend-dot selected"></i> Selected</span>
        </div>

        <div class="availability-scroll" v-if="hasRooms">
            <div v-for="group in groupedRooms" :key="group.id" class="room-group mb-3">
                <div class="room-group-head">
                    <span class="room-group-name">{{ group.name }}</span>
                    <span class="room-group-count">{{ group.rooms.length }} rooms</span>
                </div>

                <div class="room-grid">
                    <div
                        v-for="room in group.rooms"
                        :key="room.id"
                        class="room-card"
                        :class="roomCardClass(room)"
                        @click="onToggleRoom(room)"
                    >
                        <div class="room-card-top">
                            <span class="room-card-name">{{ room.name }}</span>
                            <button
                                v-if="isRoomSelected(room.id)"
                                type="button"
                                class="room-card-remove"
                                title="Unselect room"
                                @click.stop="removeRoomById(room.id)"
                            >
                                <i class="fa fa-times"></i>
                            </button>
                        </div>
                        <span v-if="room.is_dirty" class="dirty-badge">
                            <i class="fa fa-broom me-1"></i>Dirty
                        </span>
                        <div class="room-card-status">{{ roomStatusLabel(room) }}</div>
                        <div class="room-card-days">
                            <span
                                v-for="day in dayColumns"
                                :key="day.key"
                                class="day-pill"
                                :class="cellClass(room, day)"
                                :title="`${day.date} — ${cellTitle(room, day)}`"
                            >{{ day.date }}</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <p v-else class="text-muted px-1">No rooms found for the selected criteria.</p>

        <Pagination :offset="offset" :state-name="paginationStateName" @onPageChange="onRoomPageChange"/>
    </div>
</template>

<script setup>
import Pagination from '@/components/atom/Pagination';
import { computed, inject, onMounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';
import handleHotel from '@/services/modules/hotel/hotel';
import handleHospital from '@/services/modules/hospital';
import useDate from '@/services/utils/day';

const props = defineProps({
    modelValue: {
        type: Array,
        default: () => [],
    },
    checkIn: {
        type: String,
        required: true,
    },
    checkOut: {
        type: String,
        required: true,
    },
    nights: {
        type: [Number, String],
        required: true,
    },
    ownRoomIds: {
        type: Array,
        default: () => [],
    },
    paginationStateName: {
        type: String,
        default: 'paginationData',
    },
    offset: {
        type: Number,
        default: 12,
    },
    showRoomType: {
        type: Boolean,
        default: true,
    },
});

const emit = defineEmits(['update:modelValue']);

const $route = useRoute();
const $router = useRouter();
const $store = useStore();
const showError = inject('showError');
const { fetchGroup } = handleHospital();
const { fetchHotelRoomAvailability, fetchHotelRoomRates } = handleHotel();
const { formatForInput } = useDate();

const loading = ref(false);
const roomTypes = ref([]);
const rooms = ref([]);
const selectedRoomType = ref(null);
const availabilityRequestId = ref(0);
const reservedStatuses = ['confirmed', 'pending'];

const selectedRooms = computed({
    get: () => props.modelValue,
    set: (value) => emit('update:modelValue', value),
});

const hasRooms = computed(() => rooms.value.length > 0);

const filteredRooms = computed(() => {
    if (! selectedRoomType.value) return rooms.value;
    return rooms.value.filter(room => String(room.group_id) === String(selectedRoomType.value));
});

const groupedRooms = computed(() => {
    const groups = [];
    const index = {};
    for (const room of filteredRooms.value) {
        const key = room.group_id ? room.group_id : 'ungrouped';
        if (! index[key]) {
            index[key] = { id: key, name: room.group_name ? room.group_name : 'Others', rooms: [] };
            groups.push(index[key]);
        }
        index[key].rooms.push(room);
    }
    return groups;
});

const parseLocalDate = (value) => {
    const datePart = String(value).split('T')[0];
    const parts = datePart.split('-');
    if (parts.length !== 3) return new Date(value);
    return new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]));
};

const dayColumns = computed(() => {
    const columns = [];
    if (! props.checkIn) return columns;
    const nights = Number(props.nights) > 0 ? Number(props.nights) : 1;
    const start = parseLocalDate(props.checkIn);
    for (let i = 0; i < nights; i++) {
        const day = new Date(start);
        day.setDate(start.getDate() + i);
        columns.push({
            key: formatForInput(day),
            date: day.toLocaleDateString('en-US', { day: '2-digit', month: 'short' }),
        });
    }
    return columns;
});

const getBaseQuery = () => {
    return `?company_id=${$route.params.companyId}`;
};

const resetRoomPage = () => {
    if (! $route.query.page) return;
    const routeQuery = Object.assign({}, $route.query);
    delete routeQuery.page;
    $router.replace({ path: $route.path, query: routeQuery });
};

const bookingOn = (room, dayKey) => {
    if (! room.bookings) return null;
    const match = room.bookings.find(booking => {
        return dayKey >= String(booking.check_in).slice(0, 10) && dayKey < String(booking.check_out).slice(0, 10);
    });
    if (! match) return null;
    return match;
};

const isBookedOn = (room, dayKey) => {
    return bookingOn(room, dayKey) !== null;
};

const isRoomAvailable = (room) => {
    if (props.ownRoomIds.includes(room.id)) return true;
    if (! room.bookings) return true;
    if (! dayColumns.value.length) return false;
    return dayColumns.value.every(day => ! isBookedOn(room, day.key));
};

const isRoomSelected = (roomId) => {
    return selectedRooms.value.some(room => String(room.service_resource_id) === String(roomId));
};

const bookingKind = (booking) => {
    if (reservedStatuses.includes(booking.registration_status)) return 'reserved';
    return 'occupied';
};

const cellClass = (room, day) => {
    const booking = bookingOn(room, day.key);
    if (booking) return `pill-${bookingKind(booking)}`;
    if (isRoomSelected(room.id)) return 'pill-selected';
    return 'pill-free';
};

const cellTitle = (room, day) => {
    const booking = bookingOn(room, day.key);
    if (! booking) return 'Available';
    return `${bookingKind(booking) === 'reserved' ? 'Reserved' : 'Occupied'} (${booking.reservation_no})`;
};

const roomStatusLabel = (room) => {
    if (isRoomSelected(room.id)) return 'Selected';
    if (isRoomAvailable(room)) return 'Available';
    const kinds = new Set();
    for (const day of dayColumns.value) {
        const booking = bookingOn(room, day.key);
        if (booking) kinds.add(bookingKind(booking));
    }
    if (kinds.has('occupied')) return 'Occupied';
    return 'Reserved';
};

const roomCardClass = (room) => {
    if (isRoomSelected(room.id)) return 'is-selected';
    if (! isRoomAvailable(room)) return 'is-booked';
    return 'is-free';
};

const addRoom = async (room) => {
    const added = {
        service_resource_id: room.id,
        name: room.name,
        rate: room.rate ? room.rate : 0,
        is_dirty: room.is_dirty ? true : false,
    };
    selectedRooms.value = [...selectedRooms.value, added];
    if (! added.rate) await applyRoomRate(added);
};

const applyRoomRate = async (room) => {
    const query = `${getBaseQuery()}&room_ids[]=${room.service_resource_id}`;
    await fetchHotelRoomRates(query).then(res => {
        if (! res.status) {
            showError(res.message);
            removeRoomById(room.service_resource_id);
            return;
        }
        if (! res.data.length) return;
        const match = res.data.find(item => String(item.service_resource_id) === String(room.service_resource_id));
        if (! match) return;
        room.rate = match.rate;
    });
};

const removeRoomById = (roomId) => {
    selectedRooms.value = selectedRooms.value.filter(room => String(room.service_resource_id) !== String(roomId));
};

const onToggleRoom = (room) => {
    if (isRoomSelected(room.id)) {
        removeRoomById(room.id);
        return;
    }
    if (! isRoomAvailable(room)) {
        showError(`${room.name} is already booked in the selected date range.`);
        return;
    }
    addRoom(room);
};

const dropUnavailableSelectedRooms = () => {
    selectedRooms.value = selectedRooms.value.filter(selected => {
        const room = rooms.value.find(item => String(item.id) === String(selected.service_resource_id));
        if (! room) return true;
        return isRoomAvailable(room);
    });
};

const getRoomTypes = async () => {
    await fetchGroup($route.params.companyId, 'places').then(res => {
        if (res.status) roomTypes.value = res.data;
    });
};

const buildAvailabilityQuery = () => {
    let query = getBaseQuery();
    if (props.checkIn) query += `&check_in=${props.checkIn}`;
    if (props.checkOut) query += `&check_out=${props.checkOut}`;
    if (selectedRoomType.value) query += `&room_type_id=${selectedRoomType.value}`;
    if ($route.query.page) query += '&page=' + $route.query.page;
    query += '&offset=' + props.offset;
    return query;
};

const setRoomPagination = (data) => {
    if (! data.data.length) return;
    $store.commit('setPaginationData', {
        stateName: props.paginationStateName,
        data: {
            records: data.total,
            perPage: data.per_page,
            currentPage: data.current_page,
        },
    });
};

const getAvailability = async () => {
    availabilityRequestId.value += 1;
    const requestId = availabilityRequestId.value;
    loading.value = true;
    await fetchHotelRoomAvailability(buildAvailabilityQuery()).then(res => {
        if (requestId !== availabilityRequestId.value) return;
        if (! res.status) {
            rooms.value = [];
            $store.commit('resetPagination', props.paginationStateName);
            dropUnavailableSelectedRooms();
            return;
        }
        rooms.value = res.data.data;
        setRoomPagination(res.data);
        dropUnavailableSelectedRooms();
    }).catch(() => {
        showError('Something went wrong');
    }).finally(() => {
        if (requestId === availabilityRequestId.value) loading.value = false;
    });
};

const onRoomPageChange = async (page) => {
    const routeQuery = Object.assign({}, $route.query);
    routeQuery.page = page;
    await $router.replace({ path: $route.path, query: routeQuery });
    await getAvailability();
};

watch(selectedRoomType, () => {
    resetRoomPage();
    getAvailability();
});

watch(() => [props.checkIn, props.checkOut], () => {
    resetRoomPage();
    getAvailability();
});

defineExpose({ getAvailability });

onMounted(async () => {
    await Promise.all([
        getRoomTypes(),
        getAvailability(),
    ]);
});
</script>

<style scoped>
.availability-legend span {
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
    font-size: 0.82rem;
    color: #4a5568;
}
.legend-dot {
    display: inline-block;
    width: 12px;
    height: 12px;
    border-radius: 50%;
}
.legend-dot.free {
    background: #22c55e;
}
.legend-dot.reserved {
    background: #f59e0b;
}
.legend-dot.occupied {
    background: #ef4444;
}
.legend-dot.selected {
    background: #3b82f6;
}
.room-group-head {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin-bottom: 0.6rem;
}
.room-group-name {
    font-weight: 700;
    font-size: 0.95rem;
    color: #1f2937;
}
.room-group-count {
    font-size: 0.72rem;
    color: #6b7280;
    background: #f1f5f9;
    padding: 0.1rem 0.5rem;
    border-radius: 999px;
}
.availability-scroll {
    max-height: 60vh;
    overflow-y: auto;
    padding-right: 0.25rem;
}
.room-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
    gap: 0.75rem;
}
.room-card {
    border: 1.5px solid #e5e7eb;
    border-radius: 12px;
    padding: 0.75rem;
    cursor: pointer;
    background: #fff;
    transition: box-shadow 0.15s ease, border-color 0.15s ease, transform 0.1s ease;
}
.room-card:hover {
    box-shadow: 0 4px 14px rgba(15, 23, 42, 0.08);
    transform: translateY(-1px);
}
.room-card.is-booked {
    background: #fef2f2;
    border-color: #fecaca;
}
.room-card.is-selected {
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.18);
    background: #eff6ff;
}
.room-card-top {
    display: flex;
    justify-content: space-between;
    align-items: center;
}
.room-card-name {
    font-weight: 700;
    font-size: 0.95rem;
    color: #111827;
}
.room-card-remove {
    border: none;
    background: #3b82f6;
    color: #fff;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 0.65rem;
    line-height: 1;
    padding: 0;
    cursor: pointer;
}
.room-card-remove:hover {
    background: #2563eb;
}
.room-card-status {
    font-size: 0.72rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.02em;
    margin-top: 0.15rem;
    color: #6b7280;
}
.dirty-badge {
    display: inline-flex;
    align-items: center;
    margin-top: 0.35rem;
    padding: 0.1rem 0.5rem;
    border-radius: 999px;
    font-size: 0.62rem;
    font-weight: 700;
    letter-spacing: 0.02em;
    text-transform: uppercase;
    color: #b7791f;
    background: #fdf1dc;
}
.room-card.is-selected .room-card-status {
    color: #2563eb;
}
.room-card.is-booked .room-card-status {
    color: #dc2626;
}
.room-card-days {
    display: flex;
    flex-wrap: wrap;
    gap: 0.25rem;
    margin-top: 0.6rem;
}
.day-pill {
    font-size: 0.65rem;
    font-weight: 600;
    padding: 0.12rem 0.4rem;
    border-radius: 6px;
    white-space: nowrap;
    border: 1px solid transparent;
}
.pill-free {
    background: #ecfdf5;
    color: #15803d;
    border-color: #bbf7d0;
}
.pill-reserved {
    background: #fffbeb;
    color: #b45309;
    border-color: #fde68a;
}
.pill-occupied {
    background: #fef2f2;
    color: #b91c1c;
    border-color: #fecaca;
}
.pill-selected {
    background: #eff6ff;
    color: #1d4ed8;
    border-color: #bfdbfe;
}
</style>
